import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { finalize, firstValueFrom } from 'rxjs';
import { FileUploadService } from 'src/app/services/file-upload/file-upload.service';
export interface UploadedFile {
  fileName: string;
  fileUrl: string;
  fileType: string;
  fileSize: number;
}
@Component({
  selector: 'app-common-upload',
  templateUrl: './common-upload.component.html',
  styleUrl: './common-upload.component.scss'
})


export class CommonUploadComponent implements OnChanges {

  // ==============================
  // COMPONENT INPUTS
  // ==============================

  @Input() label: string = 'Upload File';

  @Input() mode: 'single' | 'multiple' = 'single';

  @Input() folderName: string = 'documents';

  @Input() accept: string =
    '.pdf,.jpg,.jpeg,.png,.webp,.bmp';

  @Input() maxFileSizeMB: number = 5;

  @Input() maxOriginalFileSizeMB: number = 30;

  @Input() disabled: boolean = false;

  // Image compression
  @Input() compressImages: boolean = true;

  @Input() imageQuality: number = 0.8;

  @Input() maxImageWidth: number = 1600;

  @Input() maxImageHeight: number = 1600;

  // Single file binding
  @Input() imageUrl: string = '';

  @Output() imageUrlChange =
    new EventEmitter<string>();

  // Multiple file binding
  @Input() files: UploadedFile[] = [];

  @Output() filesChange =
    new EventEmitter<UploadedFile[]>();

  // Other output events
  @Output() uploadError =
    new EventEmitter<string>();

  @Output() uploadingChange =
    new EventEmitter<boolean>();

  // ==============================
  // COMPONENT VARIABLES
  // ==============================

  uploadedFiles: UploadedFile[] = [];

  isUploading: boolean = false;

  errorMessage: string = '';

  uploadMessage: string = '';

  constructor(
    private uploadService: FileUploadService
  ) { }

  // ==============================
  // HANDLE PARENT INPUT CHANGES
  // ==============================

  ngOnChanges(changes: SimpleChanges): void {

    if (this.isUploading) {
      return;
    }

    if (this.mode === 'single') {

      if (changes['imageUrl'] || changes['mode']) {
        this.syncSingleFile();
      }

    } else {

      if (changes['files'] || changes['mode']) {
        this.uploadedFiles = [...(this.files || [])];
      }
    }
  }

  // ==============================
  // SYNC SINGLE IMAGE
  // ==============================

  private syncSingleFile(): void {

    if (!this.imageUrl) {
      this.uploadedFiles = [];
      return;
    }

    const existingFile = this.uploadedFiles.find(
      x => x.fileUrl === this.imageUrl
    );

    if (existingFile) {
      this.uploadedFiles = [existingFile];
      return;
    }

    const urlWithoutQuery = this.imageUrl.split('?')[0];

    let fileName = 'Uploaded File';

    try {
      fileName = decodeURIComponent(
        urlWithoutQuery.split('/').pop() || 'Uploaded File'
      );
    } catch {
      fileName = urlWithoutQuery.split('/').pop() || 'Uploaded File';
    }

    this.uploadedFiles = [{
      fileName: fileName,
      fileUrl: this.imageUrl,
      fileType: this.getFileType(fileName),
      fileSize: 0
    }];
  }

  // ==============================
  // FILE SELECTION
  // ==============================

  async onFilesSelected(event: Event): Promise<void> {

    const input = event.target as HTMLInputElement;

    const selectedFiles = Array.from(input.files || []);

    input.value = '';

    if (!selectedFiles.length) {
      return;
    }

    if (this.disabled || this.isUploading) {
      return;
    }

    this.errorMessage = '';

    const filesToUpload = this.mode === 'single'
      ? selectedFiles.slice(0, 1)
      : selectedFiles;

    await this.uploadFiles(filesToUpload);
  }

  // ==============================
  // MAIN UPLOAD METHOD
  // ==============================

  private async uploadFiles(
    selectedFiles: File[]
  ): Promise<void> {

    this.setUploading(true);

    const errors: string[] = [];

    try {

      for (const originalFile of selectedFiles) {

        try {

          // Validate original file type
          const typeError = this.validateFileType(originalFile);

          if (typeError) {
            throw new Error(typeError);
          }

          // Original file size safety limit
          const originalMaxBytes =
            this.maxOriginalFileSizeMB * 1024 * 1024;

          if (originalFile.size > originalMaxBytes) {
            throw new Error(
              `Original file size exceeds ${this.maxOriginalFileSizeMB} MB.`
            );
          }

          // Prepare file
          let fileToUpload = originalFile;

          // Compress supported images
          if (
            this.compressImages &&
            this.isCompressibleImage(originalFile)
          ) {

            this.uploadMessage =
              `Optimizing ${originalFile.name}...`;

            fileToUpload = await this.compressImage(originalFile);
          }

          // Validate final size
          const maxBytes =
            this.maxFileSizeMB * 1024 * 1024;

          if (fileToUpload.size > maxBytes) {
            throw new Error(
              `File size exceeds ${this.maxFileSizeMB} MB.`
            );
          }

          // Upload to server
          this.uploadMessage =
            `Uploading ${fileToUpload.name}...`;

          const response = await firstValueFrom(
            this.uploadService.uploadSingleFile(
              fileToUpload,
              this.folderName
            )
          );

          const uploadedUrl =
            response?.publicUrl || '';

          if (!uploadedUrl) {
            throw new Error(
              'Upload failed. File URL was not returned.'
            );
          }

          const uploadedFile: UploadedFile = {
            fileName: fileToUpload.name,
            fileUrl: uploadedUrl,
            fileType: fileToUpload.type,
            fileSize: fileToUpload.size
          };

          // Handle based on mode
          if (this.mode === 'single') {

            this.uploadedFiles = [uploadedFile];

            this.imageUrl = uploadedFile.fileUrl;

            this.imageUrlChange.emit(this.imageUrl);

          } else {

            this.uploadedFiles = [
              ...this.uploadedFiles,
              uploadedFile
            ];

            this.files = [...this.uploadedFiles];

            this.filesChange.emit([...this.files]);
          }

        } catch (error: any) {

          console.error(
            'File upload error:',
            originalFile.name,
            error
          );

          errors.push(
            `${originalFile.name}: ${error?.message || 'Upload failed.'}`
          );
        }
      }

    } finally {

      this.uploadMessage = '';

      this.setUploading(false);

      if (errors.length) {

        this.errorMessage = errors.join('\n');

        this.uploadError.emit(this.errorMessage);
      }
    }
  }

  // ==============================
  // FILE TYPE VALIDATION
  // ==============================

  private validateFileType(file: File): string {

    const allowedValues = this.accept
      .split(',')
      .map(x => x.trim().toLowerCase())
      .filter(Boolean);

    if (!allowedValues.length) {
      return '';
    }

    const fileName = file.name.toLowerCase();
    const fileMime = file.type.toLowerCase();

    const isAllowed = allowedValues.some(value => {

      if (value.startsWith('.')) {
        return fileName.endsWith(value);
      }

      if (value.endsWith('/*')) {
        return fileMime.startsWith(value.slice(0, -1));
      }

      return fileMime === value;
    });

    if (!isAllowed) {
      return 'Unsupported file type.';
    }

    const expectedMime: Record<string, string[]> = {
      '.jpg': ['image/jpeg'],
      '.jpeg': ['image/jpeg'],
      '.png': ['image/png'],
      '.webp': ['image/webp'],
      '.pdf': ['application/pdf']
    };

    const extension =
      '.' + (fileName.split('.').pop() || '');

    if (
      expectedMime[extension] &&
      fileMime &&
      !expectedMime[extension].includes(fileMime)
    ) {
      return 'File extension and content type do not match.';
    }

    return '';
  }

  // ==============================
  // CHECK IMAGE TYPE
  // ==============================

  private isCompressibleImage(file: File): boolean {

    return [
      'image/jpeg',
      'image/png',
      'image/webp'
    ].includes(file.type);
  }

  // ==============================
  // CONVERT IMAGE TO WEBP
  // ==============================

  private async compressImage(file: File): Promise<File> {

    const objectUrl = URL.createObjectURL(file);

    try {

      const image = new Image();

      image.src = objectUrl;

      await image.decode();

      const originalWidth = image.naturalWidth;
      const originalHeight = image.naturalHeight;

      if (!originalWidth || !originalHeight) {
        throw new Error('Invalid image dimensions.');
      }

      // Maintain aspect ratio
      const scale = Math.min(
        1,
        this.maxImageWidth / originalWidth,
        this.maxImageHeight / originalHeight
      );

      const width = Math.max(
        1,
        Math.round(originalWidth * scale)
      );

      const height = Math.max(
        1,
        Math.round(originalHeight * scale)
      );

      const canvas = document.createElement('canvas');

      canvas.width = width;
      canvas.height = height;

      const context = canvas.getContext('2d');

      if (!context) {
        throw new Error('Canvas is not supported.');
      }

      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';

      context.drawImage(
        image,
        0,
        0,
        width,
        height
      );

      // Restrict quality to valid range
      const quality = Math.max(
        0.1,
        Math.min(1, this.imageQuality)
      );

      const blob = await new Promise<Blob>(
        (resolve, reject) => {

          canvas.toBlob(
            result => {

              if (result) {
                resolve(result);
              } else {
                reject(
                  new Error('Image compression failed.')
                );
              }

            },
            'image/webp',
            quality
          );
        }
      );

      if (blob.type !== 'image/webp') {
        throw new Error(
          'WebP conversion is not supported by this browser.'
        );
      }

      // Keep original if WebP is larger
      if (blob.size >= file.size) {
        return file;
      }

      const fileName =
        file.name.replace(/\.[^.]+$/, '') + '.webp';

      return new File(
        [blob],
        fileName,
        {
          type: 'image/webp',
          lastModified: Date.now()
        }
      );

    } finally {

      URL.revokeObjectURL(objectUrl);
    }
  }

  // ==============================
  // REMOVE FILE
  // ==============================

  removeFile(index: number): void {

    if (this.disabled || this.isUploading) {
      return;
    }

    if (this.mode === 'single') {

      this.uploadedFiles = [];

      this.imageUrl = '';

      this.imageUrlChange.emit('');

    } else {

      this.uploadedFiles = this.uploadedFiles.filter(
        (_, i) => i !== index
      );

      this.files = [...this.uploadedFiles];

      this.filesChange.emit([...this.files]);
    }
  }

  // ==============================
  // OPEN UPLOADED FILE
  // ==============================

  openFile(file: UploadedFile): void {

    if (!file.fileUrl) return;

    window.open(
      file.fileUrl,
      '_blank',
      'noopener,noreferrer'
    );
  }

  // ==============================
  // GET FILE TYPE FROM NAME
  // ==============================

  private getFileType(fileName: string): string {

    const extension =
      fileName.split('.').pop()?.toLowerCase();

    switch (extension) {

      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';

      case 'png':
        return 'image/png';

      case 'webp':
        return 'image/webp';

      case 'pdf':
        return 'application/pdf';

      default:
        return '';
    }
  }

  // ==============================
  // PREVIEW TYPE HELPERS
  // ==============================

  isImage(file: UploadedFile): boolean {

    return (
      file.fileType?.startsWith('image/') ||
      /\.(jpg|jpeg|png|webp)$/i.test(file.fileName)
    );
  }

  isPdf(file: UploadedFile): boolean {

    return (
      file.fileType === 'application/pdf' ||
      /\.pdf$/i.test(file.fileName)
    );
  }

  // ==============================
  // FILE SIZE FORMAT
  // ==============================

  formatFileSize(bytes: number): string {

    if (!bytes) {
      return '';
    }

    if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(1) + ' KB';
    }

    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  }

  // ==============================
  // ACCEPTED TYPE DISPLAY
  // ==============================

  getAcceptedFileTypes(): string {

    return this.accept
      .split(',')
      .map(x => x.trim())
      .filter(x => x.startsWith('.'))
      .map(x => x.substring(1).toUpperCase())
      .join(', ');
  }

  // ==============================
  // LOADING STATE
  // ==============================

  private setUploading(value: boolean): void {

    this.isUploading = value;

    this.uploadingChange.emit(value);
  }
}